import { useAssistantState, useAssistantToolUI } from "@assistant-ui/react";
import { Box, Flex, Spinner } from "@chakra-ui/react";
import React, { ReactElement, useEffect, useMemo } from "react";

import { CopyableChart } from "../../../../components/ai-elements";
import { useCustomInsightsChat } from "./CustomInsightsChatProvider";
import { type ChartArgs, parseChartArgs } from "./customInsightsChatUtils";
import { LoadingStatusLabel } from "./InsightsChatMessages";

/* eslint-disable camelcase -- tool name matches backend */

type RenderChartToolProps = {
  args?: ChartArgs | null;
  status: { type: string };
  toolCallId?: string;
};

function RenderChartToolContent(
  props: RenderChartToolProps
): ReactElement | null {
  const { args: rawArgs, status, toolCallId } = props;
  const messageId = useAssistantState((s) => s.message.id);
  const { registerChartMessage } = useCustomInsightsChat();

  useEffect(() => {
    if (status.type === "complete" && rawArgs && toolCallId && messageId) {
      const args = parseChartArgs(rawArgs);
      if (args) {
        const hasData =
          (args.values?.length ?? 0) > 0 || (args.series?.length ?? 0) > 0;
        if (args.labels?.length && hasData) {
          registerChartMessage(messageId);
        }
      }
    }
  }, [status.type, rawArgs, toolCallId, messageId, registerChartMessage]);

  if (status.type === "running") {
    return (
      <Flex
        alignItems="center"
        gap="6px"
        px={4}
        py="2px"
        data-copy-role="tool-status"
      >
        <Spinner size="xs" color="purple.400" flexShrink={0} />
        <LoadingStatusLabel label="Rendering chart" />
      </Flex>
    );
  }

  if (status.type === "complete" && rawArgs) {
    const parsed = parseChartArgs(rawArgs);
    if (parsed?.labels?.length) {
      return (
        <Box mt={1}>
          <CopyableChart
            data={{
              title: parsed.title,
              type: parsed.chart_type ?? "bar",
              labels: parsed.labels,
              values: parsed.values,
              series: parsed.series,
            }}
          />
        </Box>
      );
    }
  }

  return null;
}

export function RenderChartToolUI(): null {
  const tool = useMemo(
    () => ({
      toolName: "render_chart",
      render: RenderChartToolContent,
    }),
    []
  );
  useAssistantToolUI(tool);
  return null;
}
/* eslint-enable camelcase */
